import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  usePersonCatalogTasks,
  useSetPersonCatalogTask,
} from '../../../../hooks/useOnboardingTasks';
import { AssignItemList } from '../../../../components/onboarding/AssignItemList';
import { AssignTaskModal } from '../../../../components/person/AssignTaskModal';
import { Button } from '../../../../components/ui/Button';
import { Pill } from '../../../../components/ui/Pill';
import { ApiError } from '../../../../lib/api';

const KIND_LABEL: Record<string, string> = { upload: 'Upload', todo: 'To-do' };

/**
 * Reusable library tasks, plus an escape hatch for the one-off ("bring your
 * passport on Monday") that does not deserve a library entry.
 */
export function TasksStep({ personId }: { personId: string }) {
  const qc = useQueryClient();
  const tasks = usePersonCatalogTasks(personId);
  const set = useSetPersonCatalogTask(personId);
  const [adHocOpen, setAdHocOpen] = useState(false);

  async function toggle(taskId: string, assigned: boolean) {
    try {
      await set.mutateAsync({ taskId, assigned });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update');
    }
  }

  return (
    <div className="space-y-4">
      <AssignItemList
        items={(tasks.data ?? []).map((t) => ({
          ...t,
          badge: (
            <Pill tone="neutral" size="sm">
              {KIND_LABEL[t.kind] ?? t.kind}
            </Pill>
          ),
        }))}
        loading={tasks.isLoading}
        pending={set.isPending}
        emptyTitle="No library tasks"
        emptyDescription="Reusable tasks live under Onboarding, Task library. You can still add a one-off below."
        onToggle={(id, assigned) => void toggle(id, assigned)}
      />

      <div className="flex items-center justify-between gap-3 rounded-token-sm border border-line/10 bg-surface-2 px-4 py-3">
        <p className="text-xs text-fg-muted">
          Need something just for this person? Add a one-off task instead of
          adding it to the library.
        </p>
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() => setAdHocOpen(true)}
        >
          Add one-off task
        </Button>
      </div>

      <AssignTaskModal
        open={adHocOpen}
        personId={personId}
        onClose={() => setAdHocOpen(false)}
        onDone={() => {
          setAdHocOpen(false);
          qc.invalidateQueries({ queryKey: ['person-onboarding', personId] });
        }}
      />
    </div>
  );
}
